package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initShashkiPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("shashki.md", FrontMatterElement.Builder { addScalar("title", "shashki"); addScalar("searchTitle", "Kotlin ride-hailing reference — Compose Wasm clients, Ktor server, sagas, server-driven UI"); addScalar("description", "A ride-hailing service written to show a whole Kotlin stack under load-bearing conditions: two Compose Wasm bundles, a Ktor server with two sagas, a map drawn in Compose, and eleven of the libraries on this site doing real work."); addScalar("library", "shashki"); }))
}

@Page("/shashki")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun ShashkiPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-is") }) {
        org.jetbrains.compose.web.dom.Text("What it is")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A reference, not a template. ")
        com.varabyte.kobweb.silk.components.navigation.Link("/mani") {
            org.jetbrains.compose.web.dom.Text("mani")
        }
        org.jetbrains.compose.web.dom.Text(" shows the stack working; shashki shows what happens when the same stack is asked to do something with money, maps, real-time positions and a design kit it did not choose — a ride-hailing service, two Compose Multiplatform bundles compiled to Wasm, a Ktor server holding two sagas, and eleven of the libraries on this site doing load-bearing work rather than appearing in a dependency list.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It is a demo the way a wind tunnel is a demo. Nothing here is deployed for the public; what it produces is the list below.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-seams-which-is-the-point") }) {
        org.jetbrains.compose.web.dom.Text("The seams, which is the point")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Three mechanisms were built at both ends and joined at neither")
        }
        org.jetbrains.compose.web.dom.Text(" — and none of them was found by a test. Backend-driven screens had a renderer on the client and a tree on the server with nothing between them; the settlement had a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("capture")
        }
        org.jetbrains.compose.web.dom.Text(", a receipt sender and a payout ledger, each written, tested and constructed by nobody; sign-in had PKCE on one side and a validator on the other, and the application called neither. Each was found by asking whether something was ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("called")
        }
        org.jetbrains.compose.web.dom.Text(", which is a question a green test suite never asks. A fourth arrived from the running stand: a driver who finished a ride stayed reserved for the life of the process, while the rider's screen kept showing a wait for that car — two answers to the word \"available\", computed in two places and compared nowhere.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The map had to be drawn.")
        }
        org.jetbrains.compose.web.dom.Text(" No published library renders vector tiles in Compose for Kotlin/Wasm, so the product reads pmtiles over ranged HTTP, decodes MVT, and paints roads, water and labels in Compose itself. That is not a boast: it is the reason the browser target survived at all, and the style is a transcription of two style documents rather than an engine that interprets them.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The saga was killed at every phase boundary.")
        }
        org.jetbrains.compose.web.dom.Text(" For both sagas, at each boundary, the test asserts what a dead process must not leave behind: no held payment, no reserved driver, no event escaping a saga that never completed, and nothing captured twice.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The screenshots are the tests.")
        }
        org.jetbrains.compose.web.dom.Text(" Every screen on this page's repository is a golden recorded on a Mac and verified byte-for-byte on Linux inside the ordinary ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("check")
        }
        org.jetbrains.compose.web.dom.Text("; the light theme doubled the suite and its first light picture of the offer card found a fare at 2.11:1 on white.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("one-number-per-claim") }) {
        org.jetbrains.compose.web.dom.Text("One number per claim")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Claim")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Number")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Where it comes from")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("routing on the city's own graph")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("2.57 ms")
                    }
                    org.jetbrains.compose.web.dom.Text(" median, 6.6 ms worst of 201")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("CityGraphMeasurement")
                    }
                    org.jetbrains.compose.web.dom.Text(", research §1.6e")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the image's start-up")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("1.5 – 3.7 s")
                    }
                    org.jetbrains.compose.web.dom.Text(" to healthy, of which the road graph is 0.18 – 0.40 s")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("three restarts of the container, B-35")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("observability, checked against itself")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("8 = 8 = 8")
                    }
                    org.jetbrains.compose.web.dom.Text(" — requests sent, metrik's delta, tracy's spans")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("B-39, on the stand")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("goldens across hosts")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("0.19 %")
                    }
                    org.jetbrains.compose.web.dom.Text(" of pixels on one extra character, against a 0.05 % tolerance")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("B-02")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the design kit's light theme")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("2.11:1")
                    }
                    org.jetbrains.compose.web.dom.Text(" for the offer card's fare on white — the defect the light goldens found")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("B-48")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every number above is traceable to a backlog item or a research section by name, and each of the fifty-two items ends with a section called ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("What it turned out to be")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-stack-and-what-is-not-in-it") }) {
        org.jetbrains.compose.web.dom.Text("The stack, and what is not in it")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Library")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("In shashki")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("How")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    com.varabyte.kobweb.silk.components.navigation.Link("/kvadrant-ui") {
                        org.jetbrains.compose.web.dom.Text("kvadrant-ui")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("used")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the base of every screen; two hooks were added to it for the design kit")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    com.varabyte.kobweb.silk.components.navigation.Link("/viddik") {
                        org.jetbrains.compose.web.dom.Text("viddik")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("used")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the acceptance gate — goldens inside ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("check")
                    }
                    org.jetbrains.compose.web.dom.Text(", and the Wasm suite in a headless browser")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    com.varabyte.kobweb.silk.components.navigation.Link("/petich") {
                        org.jetbrains.compose.web.dom.Text("petich")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("used")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("two sagas on one engine, killed at every phase boundary")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    com.varabyte.kobweb.silk.components.navigation.Link("/booblik") {
                        org.jetbrains.compose.web.dom.Text("booblik")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("used")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("a ride's events out of the outbox, one consumer sharing nothing with the saga")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    com.varabyte.kobweb.silk.components.navigation.Link("/kompot") {
                        org.jetbrains.compose.web.dom.Text("kompot")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("used")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("one screen the server owns, and a degradation sink that reports what a client could not draw")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    com.varabyte.kobweb.silk.components.navigation.Link("/shildik") {
                        org.jetbrains.compose.web.dom.Text("shildik")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("used")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("both bundles sign in; the rider's and the driver's routes are behind the token")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    com.varabyte.kobweb.silk.components.navigation.Link("/katcher") {
                        org.jetbrains.compose.web.dom.Text("katcher")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("used")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("browser crash reports over the ingest endpoint, no client library needed")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    com.varabyte.kobweb.silk.components.navigation.Link("/metrik") {
                        org.jetbrains.compose.web.dom.Text("metrik")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    com.varabyte.kobweb.silk.components.navigation.Link("/tracy") {
                        org.jetbrains.compose.web.dom.Text("tracy")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("used")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("named spans per saga phase, and the two collectors checked against each other")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    com.varabyte.kobweb.silk.components.navigation.Link("/smtpkn") {
                        org.jetbrains.compose.web.dom.Text("smtpkn")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("used, on the JVM")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the receipt — its first JVM consumer")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    com.varabyte.kobweb.silk.components.navigation.Link("/bochka") {
                        org.jetbrains.compose.web.dom.Text("bochka")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("a host, not a dependency")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("it serves the tile archive over public ranged HTTP; the browser is the client, and a Kotlin client would have nothing to do")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    com.varabyte.kobweb.silk.components.navigation.Link("/s3kn") {
                        org.jetbrains.compose.web.dom.Text("s3kn")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("not in the graph")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the same finding as bochka's, from the other side: the one scenario left for it is driver onboarding, and that is an open question rather than a plan")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    com.varabyte.kobweb.silk.components.navigation.Link("/telek") {
                        org.jetbrains.compose.web.dom.Text("telek")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("not used")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("it builds Telegram bots as state machines; alerting is metrik's own notifier. An item assumed otherwise and had to say so")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-does-not-show") }) {
        org.jetbrains.compose.web.dom.Text("What it does not show")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("An empty backlog is not a finished product, and the repository says where the edge is.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("One replica")
        }
        org.jetbrains.compose.web.dom.Text(", because the geo-index and the offer board live in one process — the chart refuses ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("replicaCount > 1")
        }
        org.jetbrains.compose.web.dom.Text(" rather than defaulting to 1 with a comment. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("No real driver position")
        }
        org.jetbrains.compose.web.dom.Text(": the driver bundle sends its configured point and says so on the screen. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("569 MB")
        }
        org.jetbrains.compose.web.dom.Text(" of image, of which the process needs about a third. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("A renderer, not an engine")
        }
        org.jetbrains.compose.web.dom.Text(": a third map style would be code, not configuration. And ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("no hosted instance")
        }
        org.jetbrains.compose.web.dom.Text(" — what exists is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docker compose up")
        }
        org.jetbrains.compose.web.dom.Text(" and an image tagged with the commit that built it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Of the design kit's eighteen screens, the product now draws about twelve. The rest are items, with the mechanism behind each one named.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-to-look-first") }) {
        org.jetbrains.compose.web.dom.Text("Where to look first")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/shashki") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/shashki")
        }
        org.jetbrains.compose.web.dom.Text(", and inside it the research document, which is the thing this repository is really about: four of the brief's assumptions turned out to be wrong when checked, and each is recorded next to the artefact that disproved it, with the wrong idea left standing beside its correction.")
    }
}
